import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdArrowBack, MdClose } from 'react-icons/md';
import API from '../../services/api';
import ResearchCrumb from './ResearchCrumb';
import PageHeading from './PageHeading';
import LoadingSpinner from '../../components/LoadingSpinner';

// Quorum, read-only — deliberately the SAME UI as the platform this data comes from.
//
// Structure, idioms and class names are matched to that system's own scores page: gold fill
// for the analyst with a grey marker for the agent, the agent → analyst pair spelled out in
// words, pool cards holding parameter rows, and a lens that switches every bar and every
// sort between score, weight and contribution.
//
// MATCHING IS THE POINT. The same figures are read in both places by the same people, so a
// second visual language would be a second thing to learn and a way for two screens to imply
// different findings from identical numbers. Where this file departs from the original it is
// noted inline with the reason — do not "improve" those departures back.
//
// Nothing here writes. This deployment holds no credential to the system that sends the
// data, and there is no route back.

export const GOLD = 'rgb(var(--brand-gold-rgb))';

export const numOrNull = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
export const fmtScore = (v) => { const n = numOrNull(v); return n == null ? '—' : n.toFixed(2); };
const fmtMetric = (v, mode) => {
  const n = numOrNull(v);
  return n == null ? '—' : mode === 'weight' ? `${(n * 100).toFixed(1)}%` : n.toFixed(2);
};

export function fmtDate(s) {
  if (!s) return '—';
  try {
    // "2 Sep 2026" — an ISO date is unambiguous but has to be decoded; this is read at a
    // glance. Day-first because the readers here are not American, and a bare numeric
    // format would be genuinely ambiguous to them.
    return new Date(s).toLocaleDateString('en-GB',
      { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return s; }
}

const POOL_LABELS = { structural: 'Structural', security_overlay: 'Security Overlay' };
export const poolLabel = (p) => POOL_LABELS[p]
  || String(p || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// 0–5 score: >=3.5 green, 2.5–3.5 amber, <2.5 red (null → muted).
export function scoreColor(v) {
  if (v === null || v === undefined) return 'text-gray-400 dark:text-gray-500';
  if (v >= 3.5) return 'text-green-600 dark:text-green-400';
  if (v >= 2.5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// Gold fill = the analyst's decision; grey dot = the agent's proposal. The bar is never
// RAG-coloured — colour lives in the numbers beside it, so the bar reads as position and the
// text reads as judgement.
export function Bar({ ai, human, max = 5 }) {
  const pct = (v) => {
    const n = numOrNull(v);
    return n == null ? null : Math.max(2, Math.min(100, (n / (max || 5)) * 100));
  };
  const pa = pct(ai);
  const ph = pct(human);
  return (
    <div className="relative h-1.5 w-full rounded bg-gray-100 dark:bg-gray-800">
      {ph != null && <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${ph}%`, background: GOLD }} />}
      {pa != null && (
        <div className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-gray-400 dark:border-gray-900"
             style={{ left: `${pa}%` }} title="agent" />
      )}
    </div>
  );
}

// In score mode both numbers carry RAG. In weight/contribution the agent is grey and the
// analyst is gold, mirroring the bar.
//
// DEPARTURE: the divergence dot is wired in here. The original defines it on a ScorePair
// component that is never rendered, so the ≥0.5 override signal never actually reaches a
// reader there. It is the single most useful thing on the row, so it is kept.
export function MetricPair({ ai, human, mode }) {
  const isScore = mode === 'score' || mode == null;
  const a = numOrNull(ai);
  const h = numOrNull(human);
  const diverge = isScore && a != null && h != null && Math.abs(a - h) >= 0.5;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
      <span className="text-gray-400">agent</span>
      <b className={`tabular-nums ${isScore ? scoreColor(a) : 'text-gray-500 dark:text-gray-400'}`}>
        {fmtMetric(ai, mode)}
      </b>
      <span className="text-gray-300 dark:text-gray-600">→</span>
      <span className="text-gray-400">analyst</span>
      <b className={`tabular-nums ${isScore ? scoreColor(h) : ''}`}
         style={isScore ? undefined : { color: GOLD }}>
        {fmtMetric(human, mode)}
      </b>
      {diverge && (
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
              title="agent & analyst diverge ≥0.5" />
      )}
    </span>
  );
}

/** A company's mark, with a monogram behind it.
 *
 * The <img> is only mounted until it fails; the monogram is always underneath, so a missing
 * or slow logo leaves a labelled tile rather than a broken-image glyph or a hole that shifts
 * the row when it finally loads. Logos are SELF-HOSTED (public/logos): pointing <img> at the
 * data vendor would tell them which companies this portal looks at, and put a third party in
 * the render path of a page that must work.
 */
export function LogoMark({ ticker, size = 28 }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      className="relative grid shrink-0 place-items-center overflow-hidden rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500 ring-1 ring-black/5 dark:bg-gray-800 dark:text-gray-400 dark:ring-white/10"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {(ticker || '?').slice(0, 2)}
      {!failed && (
        <img
          src={`/logos/${ticker}.png`} alt="" loading="lazy" onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full bg-white object-contain p-0.5 dark:bg-white"
        />
      )}
    </span>
  );
}

/** The score, and what it is out of. A bare 4.41 asks the reader to remember the scale. */
export function Score({ v, size = 'sm' }) {
  const n = numOrNull(v);
  if (n == null) return <span className="text-gray-400">—</span>;
  return (
    <span className="whitespace-nowrap tabular-nums">
      <span className={`font-semibold ${scoreColor(n)} ${size === 'lg' ? 'text-xl' : ''}`}>
        {n.toFixed(2)}
      </span>
      <span className="ml-0.5 text-[10px] font-normal text-gray-400">/5</span>
    </span>
  );
}

// These are context, not findings. At text-2xl serif a one-digit count and a date were set
// at the same weight as the scores themselves, and "4 Sep 2026" at that size read as a
// headline while saying almost nothing. Sized down so the strip reads as a caption above the
// data rather than competing with it.
const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-gray-200 px-3 py-2.5 dark:border-gray-800">
    <div className="text-[9.5px] font-medium uppercase tracking-[0.15em] text-gray-400">{label}</div>
    <div className="mt-0.5 truncate font-serif text-[15px] leading-snug tabular-nums text-gray-900 sm:text-base dark:text-gray-100">
      {value}
    </div>
  </div>
);

// The securities table. Pool columns are DERIVED from the data in first-seen order, never
// hardcoded: a new pool must appear on its own, and a renamed one must not leave a column
// of dashes behind.
//
// Scorer and team are deliberately absent. They are individual identities, and this portal
// is read by people outside the scoring team; the score is the product here, not who typed
// it. The sending platform shows them because its readers ARE the scorers.
function poolNamesOf(items) {
  const seen = [];
  for (const r of items) {
    for (const p of r.pool_scores || []) {
      if (p.pool && !seen.includes(p.pool)) seen.push(p.pool);
    }
  }
  return seen;
}

const poolScore = (r, name) => numOrNull(
  (r.pool_scores || []).find((p) => p.pool === name)?.score_human,
);

// The active column is stated three ways: the accent on the label, the direction arrow, and a
// rule under the header cell. A lone arrow at 11px was too small to answer "what is this list
// ordered by?" from a glance at the top of the table — which is the only question a header row
// exists to answer once it has been clicked.
//
// The rule is an inset shadow rather than a border: a border appears in the box model and would
// add a pixel to the header row the moment a column went active, nudging the whole table down.
const Th = ({ label, colKey, sort, onSort, align = 'left' }) => {
  const on = sort.key === colKey;
  return (
    <th
      onClick={() => onSort(colKey)}
      aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`cursor-pointer select-none whitespace-nowrap px-2 py-2 transition-colors ${
        on ? 'text-brand-gold' : 'hover:text-gray-600 dark:hover:text-gray-300'} ${
        align === 'right' ? 'text-right' : 'text-left'}`}
      style={on ? { boxShadow: `inset 0 -2px 0 0 ${GOLD}` } : undefined}
      title="Sort by this column"
    >
      <span className={`text-[11px] uppercase tracking-wider ${on ? 'font-semibold' : ''}`}>
        {label}
      </span>
      <span aria-hidden="true">{on ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}</span>
    </th>
  );
};

/** Sorting on a phone.
 *
 * The column headers ARE the control on a desktop; a phone gets cards and so has no header row
 * to click, which left the card list with no way to reorder it at all. This is not a header row
 * shrunk down — it is the chip row this page already uses for sector coverage, so it is one
 * gesture to learn rather than two, and it holds any number of pools without changing shape.
 *
 * It drives the SAME `sort` state as the table and therefore the same comparator and the same
 * sorted array — there is one ordered list and both views render it, so the two can never
 * disagree about the order. Tapping the active chip reverses it, which is exactly what clicking
 * an already-active column header does.
 */
function SortChips({ cols, sort, onSort }) {
  return (
    <div className="mb-3 sm:hidden">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
        Sort by
      </div>
      <div className="scrollbar-thin -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {cols.map((c) => {
          const on = sort.key === c.key;
          return (
            <button
              key={c.key} type="button" onClick={() => onSort(c.key)} aria-pressed={on}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors ${on
                ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                : 'border-gray-200 text-gray-600 dark:border-gray-800 dark:text-gray-300'}`}
            >
              <span className="whitespace-nowrap">{c.label}</span>
              {on && <span className="text-[10px]" aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScoresTable({ items, onOpen }) {
  const pools = useMemo(() => poolNamesOf(items), [items]);
  const [sort, setSort] = useState({ key: 'known_at', dir: 'desc' });

  const onSort = (key) => setSort((cur) => (cur.key === key
    ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
    // Text reads naturally A-Z; a score or a date is most useful biggest-first.
    : { key, dir: ['ticker', 'name', 'sector'].includes(key) ? 'asc' : 'desc' }));

  const value = useCallback((r, key) => {
    if (key === 'ticker') return r.ticker || '';
    if (key === 'name') return r.security_name || '';
    if (key === 'sector') return r.sector || '';
    if (key === 'known_at') return r.known_at || '';
    return poolScore(r, key.slice(5));
  }, []);

  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const x = value(a, sort.key);
      const y = value(b, sort.key);
      // A missing score sorts last in BOTH directions. Letting null ride as -Infinity would
      // put unscored names at the top of an ascending sort as though they scored worst.
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x).localeCompare(String(y)) * dir;
    });
  }, [items, sort, value]);

  // One declaration of what is sortable, feeding both the header row and the phone chips, in
  // the table's own column order so the two views name the same fields in the same sequence.
  const sortCols = useMemo(() => [
    { key: 'ticker', label: 'Ticker' },
    { key: 'name', label: 'Company' },
    { key: 'sector', label: 'Sector' },
    ...pools.map((pn) => ({ key: `pool:${pn}`, label: poolLabel(pn) })),
    { key: 'known_at', label: 'Last updated' },
  ], [pools]);

  return (
    <>
      {/* A phone gets cards. The table needs 720px to hold its columns, so at 375 it was a
          sideways-scrolling grid where the ticker scrolled out of view before the scores
          arrived — the two things a reader is comparing could not be seen at once. */}
      <SortChips cols={sortCols} sort={sort} onSort={onSort} />
      <div className="flex flex-col gap-2 sm:hidden">
        {sorted.map((r) => (
          <button
            key={`m-${r.ticker}-${r.scored_by_name}`} type="button" onClick={() => onOpen(r)}
            className="rounded-xl border border-gray-200 p-3 text-left transition-all hover:border-brand-gold hover:shadow-sm dark:border-gray-800"
          >
            <span className="flex items-center gap-2.5">
              <LogoMark ticker={r.ticker} size={32} />
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-gray-900 dark:text-gray-100">{r.ticker}</span>
                <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">
                  {r.security_name || '—'}
                </span>
              </span>
            </span>
            <span className="mt-2.5 grid grid-cols-2 gap-2">
              {pools.map((pn) => (
                <span key={pn} className="rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-gray-800/40">
                  <span className="block truncate text-[9.5px] uppercase tracking-[0.15em] text-gray-400">
                    {poolLabel(pn)}
                  </span>
                  <span className="mt-0.5 block"><Score v={poolScore(r, pn)} /></span>
                </span>
              ))}
            </span>
            {/* Classification and dating, on one metadata line under the scores.
                The date used to sit in the top row beside the ticker, where a full
                "2 Sep 2026" next to a 16px ticker read as a second headline figure and
                competed with the name for the width a phone does not have. It is what a
                reader checks last, so it is set last, small and unemphasised — subordinate
                to the ticker and the scores, which are what the card is for. */}
            <span className="mt-2 flex items-baseline justify-between gap-2 text-[10px] text-gray-400 dark:text-gray-500">
              <span className="truncate">{r.sector || ''}</span>
              <span className="shrink-0 text-[10px] tabular-nums text-gray-400">
                {fmtDate(r.known_at)}
              </span>
            </span>
          </button>
        ))}
      </div>

    <div className="scrollbar-thin hidden overflow-x-auto sm:block">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <Th label="Ticker" colKey="ticker" sort={sort} onSort={onSort} />
            <Th label="Company" colKey="name" sort={sort} onSort={onSort} />
            <Th label="Sector" colKey="sector" sort={sort} onSort={onSort} />
            {pools.map((pn) => (
              <Th key={pn} label={poolLabel(pn)} colKey={`pool:${pn}`} sort={sort}
                  onSort={onSort} align="right" />
            ))}
            <Th label="Last updated" colKey="known_at" sort={sort} onSort={onSort} align="right" />
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={`${r.ticker}-${r.scored_by_name}`} onClick={() => onOpen(r)}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40"
            >
              <td className="whitespace-nowrap px-2 py-2.5">
                <span className="flex items-center gap-2.5">
                  <LogoMark ticker={r.ticker} />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{r.ticker}</span>
                </span>
              </td>
              <td className="px-2 py-2.5 text-gray-700 dark:text-gray-300">{r.security_name || '—'}</td>
              <td className="px-2 py-2 text-gray-500 dark:text-gray-400">{r.sector || '—'}</td>
              {pools.map((pn) => {
                const v = poolScore(r, pn);
                return <td key={pn} className="px-2 py-2.5 text-right"><Score v={v} /></td>;
              })}
              {/* Metadata, not a figure — set below the row's body size so it does not read
                  at the same weight as the scores it sits beside. */}
              <td className="whitespace-nowrap px-2 py-2 text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                {fmtDate(r.known_at)}
              </td>
              <td className="whitespace-nowrap px-2 py-2 text-right text-xs" style={{ color: GOLD }}>
                View →
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

// --- the lens ------------------------------------------------------------------------

export const METRICS = [
  { key: 'score', label: 'Score' },
  { key: 'weight', label: 'Weight' },
  { key: 'contribution', label: 'Contribution' },
];

export const LENS = {
  score: '0–5 assessment',
  weight: 'decision weight within pool',
  contribution: 'score × weight',
};

const sumW = (subs, key) => (subs || []).reduce((t, s) => t + (numOrNull(s[key]) || 0), 0);

function metricSub(s, mode) {
  if (mode === 'weight') {
    return { ai: numOrNull(s.decision_weight_ai), human: numOrNull(s.decision_weight_human) };
  }
  if (mode === 'contribution') {
    const c = (a, b) => {
      const x = numOrNull(a); const y = numOrNull(b);
      return x != null && y != null ? x * y : null;
    };
    return { ai: c(s.score_ai, s.decision_weight_ai), human: c(s.score_human, s.decision_weight_human) };
  }
  return { ai: numOrNull(s.score_ai), human: numOrNull(s.score_human) };
}

// A parameter's weight is the SUM of its sub-parameters' weights.
//
// DEPARTURE, and it is load-bearing: this feed does not carry sub-parameters yet, so that sum
// would be 0 for every row and both non-score lenses would render an empty page. Where a row
// has no children, its OWN weight is used — which is right, because a childless row here is
// itself a leaf. Delete this fallback only if leaves stop carrying their own weight.
function metricParam(p, subs, mode) {
  const leaf = !subs || subs.length === 0;
  if (mode === 'weight') {
    return leaf
      ? { ai: numOrNull(p.decision_weight_ai), human: numOrNull(p.decision_weight_human) }
      : { ai: sumW(subs, 'decision_weight_ai'), human: sumW(subs, 'decision_weight_human') };
  }
  if (mode === 'contribution') {
    const wa = leaf ? numOrNull(p.decision_weight_ai) : sumW(subs, 'decision_weight_ai');
    const wh = leaf ? numOrNull(p.decision_weight_human) : sumW(subs, 'decision_weight_human');
    const c = (s, w) => { const x = numOrNull(s); return x != null && w != null ? x * w : null; };
    return { ai: c(p.score_ai, wa), human: c(p.score_human, wh) };
  }
  return { ai: numOrNull(p.score_ai), human: numOrNull(p.score_human) };
}

function SubparamRow({ s, mode, max }) {
  const w = numOrNull(s.decision_weight_human) ?? numOrNull(s.decision_weight_ai);
  const mat = numOrNull(s.raw_materiality_human) ?? numOrNull(s.raw_materiality_ai);
  const m = metricSub(s, mode);
  return (
    <div className="grid grid-cols-[1fr_10rem] items-center gap-3 py-1.5 pl-4 pr-2 text-xs">
      <div className="min-w-0">
        <div className="truncate text-gray-700 dark:text-gray-300">{s.label || s.key}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-400">
          <span className="font-mono">{s.key}</span>
          {mat != null && <span>materiality {mat}/5</span>}
          {w != null && <span>weight {(w * 100).toFixed(1)}%</span>}
          {(s.maps_to || []).length > 1 && <span>maps to {s.maps_to.join(', ')}</span>}
        </div>
      </div>
      <div>
        <Bar ai={m.ai} human={m.human} max={mode === 'score' ? 5 : max} />
        <div className="mt-1 text-right"><MetricPair ai={m.ai} human={m.human} mode={mode} /></div>
      </div>
    </div>
  );
}

function ParamRow({ p, subs, mode, max }) {
  const [open, setOpen] = useState(false);
  const m = metricParam(p, subs, mode);
  const ordered = useMemo(() => {
    const val = (s) => numOrNull(metricSub(s, mode).human) ?? -1;
    return [...subs].sort((a, b) => val(b) - val(a));
  }, [subs, mode]);
  const subMax = Math.max(
    0.0001,
    ...ordered.map((s) => numOrNull(metricSub(s, mode).human) || 0),
    ...ordered.map((s) => numOrNull(metricSub(s, mode).ai) || 0),
  );
  const has = ordered.length > 0;

  return (
    <div className="border-t border-gray-100 dark:border-gray-800/60">
      <button
        type="button" onClick={has ? () => setOpen((v) => !v) : undefined}
        aria-expanded={has ? open : undefined}
        className={`grid w-full grid-cols-[1fr_auto] items-center gap-3 py-2 text-left ${has ? '' : 'cursor-default'}`}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="shrink-0 font-mono text-[11px] text-gray-400">{p.key}</span>
            <span className="truncate text-sm text-gray-800 dark:text-gray-200">{p.label}</span>
          </div>
          <div className="mt-1"><Bar ai={m.ai} human={m.human} max={mode === 'score' ? 5 : max} /></div>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <MetricPair ai={m.ai} human={m.human} mode={mode} />
          {has && <span className="text-[10px] text-gray-400">{ordered.length} sub {open ? '▾' : '▸'}</span>}
        </div>
      </button>
      {open && has && (
        <div className="mb-2 rounded-lg bg-gray-50 dark:bg-gray-800/30">
          {ordered.map((s, i) => <SubparamRow key={i} s={s} mode={mode} max={subMax} />)}
        </div>
      )}
    </div>
  );
}

export function PoolSection({ pool, params, subs, mode }) {
  // pool → parameters by the `pool` field; parameter → sub-parameters by `parent_key`.
  // Both joins are the sender's own shape.
  // Parameters in this pool, PLUS any sub-parameter that cannot be filed under one.
  //
  // A sub is shown under its parent, so a sub whose parent_key matches nothing in this pool
  // would render nowhere at all — a scored row disappearing from the page while the payload
  // still looked complete, which is the worst failure available here because there is no
  // symptom. It is promoted to the top level instead. Verified against production as
  // never firing today; it exists for the push where it does.
  const ps = useMemo(() => {
    const own = params.filter((p) => p.pool === pool.pool);
    const keys = new Set(own.map((p) => p.key));
    const orphans = subs.filter((s2) => s2.pool === pool.pool && !keys.has(s2.parent_key));
    return [...own, ...orphans];
  }, [params, subs, pool.pool]);
  const subsFor = useCallback((p) => subs.filter((s) => s.parent_key === p.key), [subs]);

  const ordered = useMemo(() => {
    const val = (p) => numOrNull(metricParam(p, subsFor(p), mode).human) ?? -1;
    return [...ps].sort((a, b) => val(b) - val(a));
  }, [ps, subsFor, mode]);

  const pmax = Math.max(
    0.0001,
    ...ordered.map((p) => numOrNull(metricParam(p, subsFor(p), mode).human) || 0),
    ...ordered.map((p) => numOrNull(metricParam(p, subsFor(p), mode).ai) || 0),
  );

  // The sender states these; fall back to counting only when it does not.
  const linked = numOrNull(pool.linked_subparameter_count)
    ?? subs.filter((s) => ps.some((p) => s.parent_key === p.key)).length;

  return (
    <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100">{poolLabel(pool.pool)}</h3>
          <div className="text-[11px] text-gray-400">
            {pool.parameter_range ? `${pool.parameter_range} · ` : ''}
            {ordered.length} parameters{linked ? ` · ${linked} subparameters` : ''}
          </div>
        </div>
        <div className="text-right">
          <div className={`font-serif text-2xl tabular-nums ${scoreColor(numOrNull(pool.score_human))}`}>
            {fmtScore(pool.score_human)}
          </div>
          <div className="text-[11px] text-gray-400">analyst · agent {fmtScore(pool.score_ai)}</div>
        </div>
      </div>
      <div className="mt-3">
        {ordered.map((p, i) => <ParamRow key={i} p={p} subs={subsFor(p)} mode={mode} max={pmax} />)}
      </div>
    </section>
  );
}

function ScorecardDetail({ row, onClose }) {
  const [mode, setMode] = useState('score');

  // Escape closes it, and the page behind is frozen while it is open. Without the lock the
  // list scrolls under the dialog on a trackpad, which makes the whole thing feel unmoored.
  useEffect(() => {
    if (!row) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };

    // The dialog becomes a history entry, so the browser's Back button and a phone's back
    // gesture close it instead of leaving the page entirely. Without this, Back on a sheet
    // that fills the screen throws the reader out of Quorum altogether — they were trying
    // to close a panel and lost the list behind it.
    window.history.pushState({ gsDialog: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      // Closing by any other route (Back button, Escape, the scrim) must also drop the
      // history entry, or the reader has to press Back twice to leave the page.
      if (window.history.state && window.history.state.gsDialog) window.history.back();
    };
  }, [row, onClose]);

  if (!row) return null;

  const pools = Array.isArray(row.pool_scores) ? row.pool_scores : [];
  // ONE flat list carrying both levels. `parent_key === null` is a top-level parameter;
  // anything else is a sub-parameter rolling into the parameter with that key. Split here
  // and nowhere else, so there is a single place the two levels are ever told apart.
  const all = Array.isArray(row.parameters) ? row.parameters : [];
  const params = all.filter((p) => p.parent_key == null);
  const subs = all.filter((p) => p.parent_key != null);

  // THROUGH A PORTAL, onto <body>. A fixed overlay is positioned against the nearest
  // ancestor that has a transform/filter/perspective, not against the viewport — so nested
  // anywhere under one, the scrim covers only part of the screen and leaves the header and
  // the page foot undimmed, which is exactly what it was doing. A portal has no ancestors
  // to be trapped by, so this cannot regress when a wrapper above it gains a transform.
  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
         onClick={onClose} role="presentation">
      {/* The scrim is its own layer so it can blur what is behind without blurring the
          panel — a filter on a shared parent would soften the dialog's own text. */}
      <div className="gs-backdrop absolute inset-0 bg-slate-900/50 backdrop-blur-[3px]" />

      {/* Bottom sheet on a phone, centred card above it. The panel is a column with its own
          scroll: a tall scorecard used to run off the bottom of the screen with the close
          button gone with it.

          dvh, NOT vh — this is why the header was being cut off. On a mobile browser `vh`
          measures the LARGE viewport, the full height including the area behind the URL
          bar, so a sheet anchored to the bottom at 92vh pushed its own top above the
          visible screen: the logo, Back and ✕ were all off-screen with no way to scroll to
          them, because the header does not scroll. `dvh` tracks the height actually
          visible, so it cannot overshoot the way vh did. 92 was never the wrong number —
          the unit was; dropping it to 85 was over-correcting for a bug already fixed, and
          left a sixth of the screen empty above the sheet. */}
      <div className="gs-panel relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl ring-1 ring-black/5 sm:max-h-[86dvh] sm:max-w-3xl sm:rounded-2xl dark:bg-gray-900 dark:ring-white/10"
           onClick={(e) => e.stopPropagation()} role="presentation">
        {/* Grab handle, phone only — it says "this pulls up from the bottom". */}
        <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-gray-300 sm:hidden dark:bg-gray-700" />
        <div className="shrink-0 border-b border-gray-100 px-5 pb-4 pt-3 dark:border-gray-800/60">
          {/* TWO ways out, because they are two intentions: Back returns to the list, ✕
              dismisses the sheet. On a panel that covers a whole phone screen a reader reaches
              for whichever matches what they meant, and offering neither is how someone ends up
              stranded on a scorecard.
              The back control was an unbordered 11px grey line sitting directly under a 2xl
              serif ticker — present in the markup and invisible in practice, which is the same
              defect as absent: beside that heading it read as incidental metadata. It is set in
              the body scale now, bordered, and sized to a real tap target, and it leads the
              header so it is the first thing the eye lands on rather than the last.
              Both call onClose, so both also pop the pushed history entry (see the effect
              above) — the two controls and the phone's back gesture all leave by one door. */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <button type="button" onClick={onClose}
                    className="-ml-1 flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[13px] font-medium text-gray-600 transition-colors hover:border-brand-gold hover:text-brand-gold dark:border-gray-700 dark:text-gray-300">
              <MdArrowBack size={16} />
              Back
            </button>
            {/* A glyph names nothing to a screen reader, hence the label. */}
            <button type="button" onClick={onClose} aria-label="Close"
                    className="-mr-1 grid h-9 w-9 shrink-0 place-items-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200">
              <MdClose size={20} />
            </button>
          </div>
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2.5 font-serif text-2xl text-gray-900 dark:text-gray-100">
              <LogoMark ticker={row.ticker} size={34} />
              {row.ticker}
              {row.security_name
                ? <span className="ml-2 text-base font-normal text-gray-400">{row.security_name}</span>
                : null}
            </h2>
            {/* Classification and dating, not identity: the scorer and team are shown on
                the sending platform, whose readers are the scorers themselves. Here the
                score is the product, so naming an individual adds nothing a reader can use
                and puts a person's name on a page they never chose to appear on. */}
            <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-gray-400">
              {row.sector ? <span>{row.sector}</span> : null}
              {row.industry ? <span>· {row.industry}</span> : null}
              <span>{row.sector || row.industry ? '· ' : ''}{fmtDate(row.known_at)}</span>
              {row.method_version ? <span>· method {row.method_version}</span> : null}
            </div>
            {numOrNull(row.overall_score_final) != null && (
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
                  Overall
                </span>
                <Score v={row.overall_score_final} size="lg" />
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-gray-100 px-5 py-3 dark:border-gray-800/60">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs dark:border-gray-800">
            {METRICS.map((mt) => (
              <button key={mt.key} type="button" onClick={() => setMode(mt.key)}
                      className="rounded-md px-2.5 py-1 font-medium transition-colors"
                      style={mode === mt.key ? { background: GOLD, color: '#111' } : { color: '#6b7280' }}>
                {mt.label}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-gray-400">{LENS[mode]}</span>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          <div className="flex flex-col gap-4">
            {pools.map((pool, i) => (
              <PoolSection key={i} pool={pool} params={params} subs={subs} mode={mode} />
            ))}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

/** Coverage by sector.
 *
 * Only non-empty sectors are tiled. `sector` is empty for names outside the classifier's
 * coverage, and bucketing those under "Unknown" would put a fabricated sector on screen
 * beside real ones; the count of unclassified names is stated separately instead.
 *
 * Counts DISTINCT tickers, not rows: one security scored by two teams is one company in a
 * coverage count, and summing rows would overstate the book.
 */
function sectorsOf(items) {
  const byName = new Map();
  let unclassified = 0;
  for (const r of items) {
    const name = (r.sector || '').trim();
    if (!name) { unclassified += 1; continue; }
    if (!byName.has(name)) byName.set(name, new Set());
    byName.get(name).add(r.ticker);
  }
  const sectors = [...byName.entries()]
    .map(([name, tickers]) => ({ name, count: tickers.size }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return { sectors, unclassified };
}

/** Sector coverage as one scrolling row.
 *
 * This was a wrapping grid of tiles. With five sectors it already broke to a second row
 * with a single tile stranded on it, and the count only goes up as coverage widens — a grid
 * that reflows every time a sector is added is not a stable thing to read. A single row that
 * scrolls holds any number without changing shape, and on a phone horizontal scroll is the
 * familiar gesture for exactly this.
 *
 * The counts are the point, so they stay; "All" is a chip rather than a separate control so
 * clearing a filter is in the same place as setting one.
 */
function SectorChips({ items, picked, onPick }) {
  const { sectors, unclassified } = useMemo(() => sectorsOf(items), [items]);
  if (!sectors.length) return null;

  const Chip = ({ on, onClick, name, count }) => (
    <button
      type="button" onClick={onClick} aria-pressed={on}
      className={`flex shrink-0 items-baseline gap-2 rounded-full border px-3 py-1.5 transition-colors ${on
        ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
        : 'border-gray-200 text-gray-600 hover:border-brand-gold dark:border-gray-800 dark:text-gray-300'}`}
    >
      <span className="whitespace-nowrap text-[13px]">{name}</span>
      <span className="tabular-nums text-[11px] opacity-70">{count}</span>
    </button>
  );

  return (
    <div>
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
        Coverage by sector
      </div>
      <div className="scrollbar-thin -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        <Chip on={!picked} onClick={() => onPick(null)} name="All" count={items.length} />
        {sectors.map((x) => (
          <Chip key={x.name} on={picked === x.name} name={x.name} count={x.count}
                onClick={() => onPick(picked === x.name ? null : x.name)} />
        ))}
      </div>
      {unclassified > 0 && (
        <p className="mt-1.5 text-[11px] text-gray-400">{unclassified} without a sector</p>
      )}
    </div>
  );
}

export default function ResearchPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(null);
  const [sector, setSector] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    API.get('/api/research-feed/scores/')
      .then((r) => { setData(r.data); setErr(''); })
      .catch(() => setErr('These scores could not be loaded.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const sectorCount = new Set(items.map((r) => (r.sector || '').trim()).filter(Boolean)).size;
  const shown = sector ? items.filter((r) => (r.sector || '').trim() === sector) : items;

  return (
    <div>
      <ResearchCrumb mode="Quorum" />

      <PageHeading title="Quorum" />

      {err && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {err}
        </div>
      )}

      {loading && <LoadingSpinner message="Loading Quorum…" />}

      {!loading && !items.length && !err && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No scorecards yet — they appear here as scorers publish.
        </p>
      )}

      {!loading && !!items.length && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Securities" value={items.length} />
            <Stat label="Sectors" value={sectorCount} />
            <Stat label="Last updated" value={fmtDate(data?.as_at)} />
          </div>

          <SectorChips items={items} picked={sector} onPick={setSector} />

          <div>
            {sector && (
              <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">
                {sector} · {shown.length} {shown.length === 1 ? 'security' : 'securities'}
              </div>
            )}
            <ScoresTable items={shown} onOpen={setSelected} />
          </div>

        </div>
      )}

      <ScorecardDetail row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

